import React from 'react';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const elements = parseMarkdown(content);
  return <>{elements}</>;
};

function parseMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule: --- or *** or ___ (3+ chars)
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      elements.push(<hr key={key++} />);
      i++;
      continue;
    }

    // Headings: # ## ###
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      const tag = level <= 2 ? 'h2' : 'h3';
      elements.push(React.createElement(tag, { key: key++ }, renderInline(headingText)));
      i++;
      continue;
    }

    // Table: starts with | and next line is separator |---|
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|[\s\-:|]+\|/.test(lines[i + 1])) {
      const headerLine = line;
      const separatorLine = lines[i + 1];
      i += 2;
      const bodyLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyLines.push(lines[i]);
        i++;
      }
      const alignments = parseAlignments(separatorLine);
      elements.push(<MarkdownTable key={key++} headerLine={headerLine} bodyLines={bodyLines} alignments={alignments} />);
      continue;
    }

    // Blockquote
    if (line.trim().startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      elements.push(
        <blockquote key={key++}>
          {quoteLines.map((ql, idx) => <p key={idx}>{renderInline(ql)}</p>)}
        </blockquote>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*[-*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={key++}>
          {listItems.map((li, idx) => <li key={idx}>{renderInline(li)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={key++}>
          {listItems.map((li, idx) => <li key={idx}>{renderInline(li)}</li>)}
        </ol>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Regular paragraph
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('|') &&
      !lines[i].trim().startsWith('>') &&
      !lines[i].match(/^#{1,3}\s/) &&
      !/^\s*[-*]\s/.test(lines[i]) &&
      !/^\s*\d+\.\s/.test(lines[i]) &&
      !/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    elements.push(<p key={key++}>{renderInline(paraLines.join('\n'))}</p>);
  }

  return elements;
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Order matters: bold (**) before single italic (*), code backticks, underscore italic
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|(?:^|(?<=\s))_(.+?)_(?:$|(?=[\s.,;:!?])))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      // `code`
      parts.push(<code key={key++}>{match[4]}</code>);
    } else if (match[5]) {
      // _italic_
      parts.push(<em key={key++}>{match[5]}</em>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

type Alignment = 'left' | 'center' | 'right';

function parseAlignments(separatorLine: string): Alignment[] {
  return separatorLine.split('|').slice(1, -1).map(cell => {
    const trimmed = cell.trim();
    if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
    if (trimmed.endsWith(':')) return 'right';
    return 'left';
  });
}

interface MarkdownTableProps {
  headerLine: string;
  bodyLines: string[];
  alignments: Alignment[];
}

const MarkdownTable: React.FC<MarkdownTableProps> = ({ headerLine, bodyLines, alignments }) => {
  const parseCells = (line: string): string[] => {
    return line.split('|').slice(1, -1).map(cell => cell.trim());
  };

  const headers = parseCells(headerLine);

  return (
    <table>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={alignments[i] !== 'left' ? { textAlign: alignments[i] } : undefined}>
              {renderInline(h)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyLines.map((row, ri) => (
          <tr key={ri}>
            {parseCells(row).map((cell, ci) => (
              <td key={ci} style={alignments[ci] !== 'left' ? { textAlign: alignments[ci] } : undefined}>
                {renderInline(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default MarkdownRenderer;
